/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable no-unused-expressions */
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ContentSection,
  Fieldset,
  Heading,
  Layout,
  PageWrap,
  SectionHeading,
} from '@vwfs-bronson/bronson-react';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { CheckboxFormField } from '../../../components/FormFields/CheckboxFormField';
import { getBrandName } from '../../../config';
import { FinancialProductType } from '../../../resources/base/models/enums';
import { getCustomer } from '../../../services/api/miles/miles-customer-services';
import {
  calculateQuote,
  createOrUpdatePrivateCustomer,
  unlinkCoCustomerFromTheQuote,
} from '../../../services/api/miles/miles-services';
import { strBoolToBool, waitForMiles } from '../../../services/common/miscUtils';
import { SetState } from '../../../services/common/miscUtils.types';
import { RootState } from '../../../services/redux';
import {
  customerSelectors,
  setCurrentCustomerId,
  setMissingMilesData,
} from '../../../services/redux/features/customer.slice';
import { loadingActions } from '../../../services/redux/features/loading.slice';
import { quoteSelectors } from '../../../services/redux/features/quote.slice';
import { useNavigate } from '../../../services/routing/navigate';
import { ProspectSearchPopupModal } from '../../ProspectSearchPopupModal/ProspectSearchPopupModal';
import { validateEmailTls } from '../utils/checkTLSUtil';
import { ConsentModal } from './ConsentModal';
import {
  BirthName,
  CarToBeReturned,
  CountryCodeMobilePhone,
  DateOfBirth,
  Email,
  FirstName,
  Gender,
  IsExistingClient,
  LastName,
  MobilePhone,
  PreviousContractNumber,
  Profession,
  TelephonePhone,
} from './fieldset';
import { getPrivateCustomerFormInitialValues, PrivateCustomerProspectForm } from './initialValues';
import { ProspectPageButtons } from './ProspectPageButtons';
import { getCreateOrUpdateCustomerRequest, sendValidationErrors } from './utils';
import { getPrivateCustomerValidationSchema } from './validationSchema';

interface Props {
  initialMilesCustomerId: string | undefined;
  quoteId: string;
}

export const PrivateProspectForm: FunctionComponent<Props> = ({ initialMilesCustomerId, quoteId }) => {
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [initialValues, setInitialValues] = useState({} as PrivateCustomerProspectForm);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<PrivateCustomerProspectForm>>();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [milesCustomerId, setMilesCustomerId] = useState(() => initialMilesCustomerId);

  const isQuoteEditable = useSelector(quoteSelectors.selectIsQuoteEditable);
  const resetFormButtonActive = isQuoteEditable && !!milesCustomerId;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const quote = useSelector(quoteSelectors.selectQuote);
  const missingMilesData = useSelector(customerSelectors.selectMissingMilesData);
  const { hasCoCustomer } = missingMilesData;
  const milesCustomerIdCoCustomer = useSelector(customerSelectors.selectCoCustomerId);

  useEffect(() => {
    if (isSubmitted) return;

    dispatch(loadingActions.setLoading(true));
    (async () => {
      let customerData = {} as CustomerIntegration.Schemas.CustomerDataByIdResponse;

      if (milesCustomerId) {
        customerData = (await getCustomer(milesCustomerId)).customerData;
      }

      setInitialValues(
        getPrivateCustomerFormInitialValues(quote, customerData, milesCustomerId ? missingMilesData : undefined)
      );
      dispatch(loadingActions.setLoading(false));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milesCustomerId]);

  useEffect(() => {
    if (isQuoteEditable) {
      setValidationSchema(getPrivateCustomerValidationSchema(t));
    } else {
      setValidationSchema(undefined);
    }
  }, [t, isQuoteEditable]);

  const onSubmit = async (
    values: PrivateCustomerProspectForm,
    { setFieldError }: FormikHelpers<PrivateCustomerProspectForm>
  ) => {
    const tlsValid = await validateEmailTls(values.email!, setFieldError, dispatch);

    if (!tlsValid) {
      return;
    }

    if (!isQuoteEditable) {
      if (hasCoCustomer) {
        navigate.form(2, '/coContractor');
      } else {
        navigate.form('/3');
      }
      return;
    }

    dispatch(loadingActions.setLoading(true));

    try {
      const calculateQuoteRequest: QuoteSchema.Schemas.CalculateSalesQuoteRequest = {};
      const createCustomerRequest = getCreateOrUpdateCustomerRequest(
        values as PrivateCustomerProspectForm,
        milesCustomerId
      );
      const newMilesCustomerId = (await createOrUpdatePrivateCustomer(createCustomerRequest, quoteId)).milesCustomerId;

      const { financialProduct } = createCustomerRequest;

      calculateQuoteRequest.financialProduct = financialProduct;

      await waitForMiles();
      await calculateQuote(quoteId, calculateQuoteRequest);

      dispatch(setCurrentCustomerId(newMilesCustomerId));
      dispatch(setMissingMilesData({ hasCoCustomer: values.hasCoCustomer, posFrOccupation: values.profession }));

      // Remove Co-customer from quote
      if (!values.hasCoCustomer && milesCustomerIdCoCustomer) {
        await unlinkCoCustomerFromTheQuote(quoteId, milesCustomerIdCoCustomer);
      }
      setIsSubmitted(true);

      setShowConsentModal(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      navigate.error();
    } finally {
      dispatch(loadingActions.setLoading(false));
    }
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      <PageWrap>
        <ContentSection altBackground={getBrandName().includes('vw')} className="u-pv-none">
          {/* eslint-disable-next-line no-use-before-define */}
          <FormContent
            milesCustomerId={milesCustomerId}
            setMilesCustomerId={setMilesCustomerId}
            resetFormButtonActive={resetFormButtonActive}
          />
          <Layout center>
            <ProspectPageButtons />
          </Layout>
        </ContentSection>
        <ConsentModal
          visible={showConsentModal}
          setShowConsentModal={setShowConsentModal}
          isCoCustomer={false}
          hasCoCustomer={hasCoCustomer}
        />
      </PageWrap>
    </Formik>
  );
};

interface FormContentProps {
  setMilesCustomerId: SetState<string | undefined>;
  resetFormButtonActive: boolean;
  milesCustomerId?: string;
}

const FormContent: FunctionComponent<FormContentProps> = ({
  setMilesCustomerId,
  milesCustomerId,
  resetFormButtonActive,
}) => {
  const [showProspectSearchModal, setShowProspectSearchModal] = useState(false);
  const { t } = useTranslation();
  const { values, errors, isSubmitting, touched } = useFormikContext<PrivateCustomerProspectForm>();

  const selectedProductType = useSelector((state: RootState) => state.productType.selectedProductType);

  const onConfirmProspectSearchModal = async ({ milesCustomerId }) => {
    setMilesCustomerId(milesCustomerId);
  };

  const isCustomerSelected = Boolean(milesCustomerId);
  const isQuoteEditable = useSelector(quoteSelectors.selectIsQuoteEditable);

  useEffect(() => {
    if (!isSubmitting) return;
    sendValidationErrors(errors, touched);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);

  return (
    <ContentSection.ComponentWrapper className="u-mt">
      <div className="u-mb@s">
        <Layout>
          <Layout.Item default="3/4" m="2/3" s="1/1">
            <SectionHeading level={2} className="u-mb-xsmall@s">
              {t('step2:heading:privateCustomer')}
            </SectionHeading>
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <Layout>
              <Layout.Item default="1/2" s="2/5" className="u-text-center">
                <Layout.Item>
                  <Button
                    key="b2"
                    icon="semantic-search"
                    ariaLabel="search"
                    secondary
                    type="button"
                    onClick={() => {
                      setShowProspectSearchModal(true);
                    }}
                    disabled={!isQuoteEditable}
                  />
                </Layout.Item>
                <Layout.Item>{t('step2:prospectPopup:buttonTitle')}</Layout.Item>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/2" className="u-text-center">
                <Layout.Item>
                  <Button
                    key="b2"
                    icon="semantic-delete"
                    ariaLabel="delete"
                    secondary
                    type="button"
                    onClick={() => {
                      setMilesCustomerId(undefined);
                    }}
                    disabled={!resetFormButtonActive}
                  />
                </Layout.Item>
                <Layout.Item>{t('step2:resetForm:buttonTitle')}</Layout.Item>
              </Layout.Item>
            </Layout>

            <ProspectSearchPopupModal
              visible={showProspectSearchModal ?? false}
              onCancel={() => {
                setShowProspectSearchModal(false);
              }}
              onConfirm={onConfirmProspectSearchModal}
            />
          </Layout.Item>
        </Layout>
      </div>
      <Fieldset disabled={!isQuoteEditable}>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">
              <Gender />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1" className="u-hide@s" />
            <Layout.Item default="1/2" s="1/1">
              <FirstName isDisabled={isCustomerSelected} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <LastName isDisabled={isCustomerSelected} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <BirthName isDisabled={isCustomerSelected} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <DateOfBirth
                labelText={t('step2:dateOfBirth:label')}
                infoText={t('step2:dateInfoText:label')}
                disabled={isCustomerSelected}
              />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <Profession />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <Email />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <CountryCodeMobilePhone />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <MobilePhone />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <TelephonePhone />
            </Layout.Item>
          </Layout>
        </Fieldset.Row>
        {selectedProductType === FinancialProductType.FINANCING && (
          <>
            <Heading level={6}>{t('step2:heading:typeOfOffer')}</Heading>
            <Fieldset.Row>
              <Layout>
                <Layout.Item default="1/2" s="1/1">
                  <IsExistingClient />
                </Layout.Item>
                {strBoolToBool(values.isExistingClient) && (
                  <>
                    <Layout.Item default="1/2" s="1/1" />
                    <Layout.Item default="1/2" s="1/1">
                      <PreviousContractNumber />
                    </Layout.Item>
                    <Layout.Item default="1/2" s="1/1">
                      <CarToBeReturned />
                    </Layout.Item>
                  </>
                )}
              </Layout>
            </Fieldset.Row>
            <>
              <CheckboxFormField fieldName="hasCoCustomer" labelTranslationKey="step2:cocontractor:label" />
              <br />
            </>
          </>
        )}
      </Fieldset>
    </ContentSection.ComponentWrapper>
  );
};
